import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import RegieberichtForm, { Regiebericht } from './RegieberichtForm';
import { navigiereZuAuftrag, kannZuAuftragNavigieren } from '../lib/navigation';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_OPTIONEN: { wert: string; label: string }[] = [
  { wert: '', label: 'Alle Status' },
  { wert: 'ENTWURF', label: 'Entwurf' },
  { wert: 'UNTERSCHRIEBEN', label: 'Unterschrieben' },
  { wert: 'ABGERECHNET', label: 'Abgerechnet' },
];

interface RegieberichteOverviewProps {
  authToken: string;
  userRolle?: string;
}

const RegieberichteOverview: React.FC<RegieberichteOverviewProps> = ({ authToken, userRolle }) => {
  const [berichte, setBerichte] = useState<Regiebericht[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [offenId, setOffenId] = useState<number | null>(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    setFehler(null);
    try {
      const res = await axios.get<Regiebericht[]>(`${API_URL}/api/regieberichte`, { headers });
      setBerichte(res.data);
    } catch {
      setFehler('Regieberichte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const gefiltert = filterStatus ? berichte.filter(b => b.status === filterStatus) : berichte;
  const offener = offenId != null ? berichte.find(b => b.id === offenId) : null;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Regieberichte</h2>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '6px 10px' }}>
          {STATUS_OPTIONEN.map(o => <option key={o.wert} value={o.wert}>{o.label}</option>)}
        </select>
      </div>
      {fehler && <div style={{ color: 'var(--red)', marginBottom: 12 }}>{fehler}</div>}
      {loading ? (
        <div>Lade Regieberichte…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bone-3)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nr</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Auftrag</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nachtrag</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Summe netto</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map(b => (
              <tr key={b.id} style={{ borderBottom: '1px solid var(--bone-3)', cursor: 'pointer' }}
                  onClick={() => setOffenId(b.id)} title="Bericht öffnen">
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{b.bericht_nr}</td>
                <td style={{ padding: '8px 12px' }}>{b.datum ? new Date(b.datum).toLocaleDateString('de-DE') : '—'}</td>
                <td style={{ padding: '8px 12px' }}>{b.status}</td>
                <td style={{ padding: '8px 12px' }}>
                  {b.auftrag_id != null ? (
                    kannZuAuftragNavigieren() ? (
                      <button className="k-btn k-btn--ghost k-btn--sm"
                              onClick={e => { e.stopPropagation(); navigiereZuAuftrag(b.auftrag_id as number); }}>
                        {b.auftrag_nr || `#${b.auftrag_id}`}
                      </button>
                    ) : (
                      // Rolle sieht den Aufträge-Tab nicht — kein toter Link.
                      <span>{b.auftrag_nr || `#${b.auftrag_id}`}</span>
                    )
                  ) : '—'}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {b.nachtrag_auftrag ? (
                    kannZuAuftragNavigieren() ? (
                      <button className="k-btn k-btn--ghost k-btn--sm"
                              onClick={e => { e.stopPropagation(); navigiereZuAuftrag(b.nachtrag_auftrag!.auftrag_id); }}>
                        → {b.nachtrag_auftrag.auftrag_nr}
                      </button>
                    ) : (
                      <span>{b.nachtrag_auftrag.auftrag_nr}</span>
                    )
                  ) : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{b.summe_netto.toFixed(2)} €</td>
              </tr>
            ))}
            {gefiltert.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Keine Regieberichte vorhanden.</td></tr>
            )}
          </tbody>
        </table>
      )}
      {offener && (
        <RegieberichtForm
          {...(offener.auftrag_id != null ? { auftragId: offener.auftrag_id } : { projektId: offener.projekt_id as number })}
          regieberichtId={offener.id}
          authToken={authToken}
          userRolle={userRolle}
          onClose={() => { setOffenId(null); load(); }}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default RegieberichteOverview;
